import { useEffect, useMemo, useState } from 'react';

import { resolveMessageForAction } from '../feed/echo/messageActions';

const NOT_REQUESTED = { message: null, isLoading: false };

// Canonical message-by-id lookup for UI that has an id but no rendered row in hand.
// Callers holding a row use it directly (see useRetainedFeedMessage); this hook is for
// the rest: reply parents outside the loaded window, activity subjects, and anything
// else that has to go to the durable feed cache (and optionally the network/legacy
// fallbacks) to find its message.
//
// `inlineMessage` short-circuits the whole lookup for callers that sometimes already
// carry the row — e.g. a renderer row whose reply parent was resolved by the feed.
export function useResolvedMessage(messageId, options = {}) {
  const { channelId = null, allowNetwork = false, allowLegacy = false, inlineMessage = null } = options;

  const normalizedId = messageId ? String(messageId) : null;
  const normalizedChannelId = channelId ? String(channelId) : null;
  // An id with no inline row is the only case that has to wait on the resolver.
  const isPending = Boolean(normalizedId) && !inlineMessage;
  const [resolution, setResolution] = useState(null);

  useEffect(() => {
    if (!isPending) return undefined;

    let cancelled = false;
    resolveMessageForAction({
      messageId: normalizedId,
      channelId: normalizedChannelId,
      allowNetwork,
      allowLegacy,
    })
      .then((result) => {
        if (!cancelled) setResolution({ id: normalizedId, message: result.state === 'found' ? result.message : null });
      })
      .catch(() => {
        // resolveMessageForAction already folds lookup failures into its resolution
        // state; a rejection here is unexpected and degrades to a miss.
        if (!cancelled) setResolution({ id: normalizedId, message: null });
      });
    return () => {
      cancelled = true;
    };
  }, [isPending, normalizedId, normalizedChannelId, allowNetwork, allowLegacy]);

  return useMemo(() => {
    if (!normalizedId) return NOT_REQUESTED;
    if (inlineMessage) return { message: inlineMessage, isLoading: false };
    // The resolution carries the id it was fetched for. Clearing it in the effect instead
    // would land a render late: a caller whose messageId changes in place (an ActivityFeed
    // row reused for another activity, a delete confirmation retargeted at a new reply
    // parent) would paint the previous message's text and author as settled for one frame.
    const current = resolution?.id === normalizedId ? resolution : null;
    return { message: current?.message ?? null, isLoading: !current };
  }, [normalizedId, inlineMessage, resolution]);
}
